(function () {
  var root = document.documentElement;
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  root.classList.add("fvh-js");

  function setDelays(section) {
    var items = section.querySelectorAll("[data-fvh-item]");
    var lines = section.querySelectorAll("[data-fvh-line]");

    items.forEach(function (item, index) {
      item.style.setProperty("--fvh-delay", Math.min(180 + index * 95, 980) + "ms");
    });

    lines.forEach(function (line, index) {
      line.style.setProperty("--fvh-delay", 420 + index * 140 + "ms");
    });
  }

  function reveal(section) {
    setDelays(section);

    if (reduceMotion || !("IntersectionObserver" in window)) {
      section.classList.add("fvh-is-visible");
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add("fvh-is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.22
      }
    );

    observer.observe(section);
  }

  function init() {
    document.querySelectorAll("[data-fvh-section]").forEach(reveal);
    document.querySelectorAll("[data-fvh-crown-canvas]").forEach(initCrown);
  }

  function initCrown(cv) {
    if (cv.dataset.fvhCrownReady === "true") {
      return;
    }

    cv.dataset.fvhCrownReady = "true";

    var ct = cv.getContext("2d");
    var CX = 160;
    var CY = 165;
    var ang = 0;
    var bob = 0;

    function nrm(v) {
      var m = Math.sqrt(v[0] ** 2 + v[1] ** 2 + v[2] ** 2) || 1;
      return v.map(function (x) {
        return x / m;
      });
    }

    function cross3(a, b) {
      return [
        a[1] * b[2] - a[2] * b[1],
        a[2] * b[0] - a[0] * b[2],
        a[0] * b[1] - a[1] * b[0]
      ];
    }

    function fN(a, b, c) {
      return nrm(
        cross3(
          [b[0] - a[0], b[1] - a[1], b[2] - a[2]],
          [c[0] - a[0], c[1] - a[1], c[2] - a[2]]
        )
      );
    }

    function dot(a, b) {
      return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    }

    var L = nrm([0.25, 0.9, -0.3]);

    function goldCol(d, e) {
      var extra = e || 0;
      var t = Math.max(0, Math.min(1, 0.3 + d * 0.7 + extra));
      return "rgb(" + Math.round(80 + t * 168) + "," + Math.round(48 + t * 112) + "," + Math.round(t * 28) + ")";
    }

    function gemCol(type, d) {
      var t = Math.max(0, Math.min(1, 0.35 + d * 0.65));
      var c = {
        r: [Math.round(80 + t * 160), Math.round(t * 30), Math.round(t * 30)],
        b: [Math.round(t * 40), Math.round(50 + t * 100), Math.round(80 + t * 160)],
        g: [Math.round(t * 40), Math.round(80 + t * 140), Math.round(t * 50)]
      }[type];
      return "rgb(" + c[0] + "," + c[1] + "," + c[2] + ")";
    }

    function rY(p, a) {
      var c = Math.cos(a);
      var s = Math.sin(a);
      return [p[0] * c - p[2] * s, p[1], p[0] * s + p[2] * c];
    }

    function pr(p) {
      return { x: CX + p[0], y: CY - p[1], z: p[2] };
    }

    var SEG = 40;
    var RO = 74;
    var RI = 62;
    var BH = 30;
    var SPIKE_H = 82;
    var SPIKE_AW = 0.72;
    var SPIKE_GEMS = ["r", "b", "g", "g", "b"];

    function ring(r, y) {
      var p = [];
      for (var i = 0; i < SEG; i += 1) {
        var a = (i / SEG) * Math.PI * 2;
        p.push([Math.cos(a) * r, y, Math.sin(a) * r]);
      }
      return p;
    }

    var rBot = ring(RO, 0);
    var rG1b = ring(RO, 8);
    var rG1i = ring(RO - 7, 10.5);
    var rG1t = ring(RO, 13);
    var rG2b = ring(RO, 17);
    var rG2i = ring(RO - 7, 19.5);
    var rG2t = ring(RO, 22);
    var rTop = ring(RO, BH);
    var rTI = ring(RI, BH);

    function buildPolys(rot) {
      var polys = [];

      function tri(a, b, c, bias, gem, gt) {
        var triBias = bias || 0;
        var isGem = gem || false;
        var gemType = gt || "r";
        var ra = rY(a, rot);
        var rb = rY(b, rot);
        var rc = rY(c, rot);
        var n = fN(ra, rb, rc);
        var pa = pr(ra);
        var pb = pr(rb);
        var pc = pr(rc);

        if ((pb.x - pa.x) * (pc.y - pa.y) - (pc.x - pa.x) * (pb.y - pa.y) > 0) {
          return;
        }

        var d = dot(n, L);
        polys.push({
          pts: [pa, pb, pc],
          z: (ra[2] + rb[2] + rc[2]) / 3,
          col: isGem ? gemCol(gemType, d) : goldCol(d, triBias),
          stroke: isGem ? "rgba(0,0,0,0.2)" : "rgba(80,45,0,0.07)"
        });
      }

      [
        [rBot, rG1b],
        [rG1b, rG1i],
        [rG1i, rG1t],
        [rG1t, rG2b],
        [rG2b, rG2i],
        [rG2i, rG2t],
        [rG2t, rTop]
      ].forEach(function (pair) {
        var lo = pair[0];
        var hi = pair[1];

        for (var i = 0; i < SEG; i += 1) {
          var j = (i + 1) % SEG;
          tri(lo[i], lo[j], hi[j]);
          tri(lo[i], hi[j], hi[i]);
        }
      });

      for (var i = 0; i < SEG; i += 1) {
        var j = (i + 1) % SEG;
        tri([0, 0, 0], rBot[j], rBot[i]);
      }

      for (var k = 0; k < SEG; k += 1) {
        var l = (k + 1) % SEG;
        tri(rTop[k], rTop[l], rTI[l], 0.1);
        tri(rTop[k], rTI[l], rTI[k], 0.1);
      }

      for (var si = 0; si < 5; si += 1) {
        var sA = (si / 5) * Math.PI * 2;
        var tipR = (RO + RI) / 2;
        var tip = [Math.cos(sA) * tipR, BH + SPIKE_H, Math.sin(sA) * tipR];
        var nF = 10;
        var ob = [];
        var ib = [];

        for (var m = 0; m <= nF; m += 1) {
          var a = sA - SPIKE_AW / 2 + m * (SPIKE_AW / nF);
          ob.push([Math.cos(a) * RO, BH, Math.sin(a) * RO]);
          ib.push([Math.cos(a) * RI, BH, Math.sin(a) * RI]);
        }

        for (var n = 0; n < nF; n += 1) {
          tri(ob[n], ob[n + 1], tip, 0.0);
        }

        for (var o = 0; o < nF; o += 1) {
          tri(ib[o + 1], ib[o], tip, -0.22);
        }

        tri(ib[0], ob[0], tip, -0.1);
        tri(ob[nF], ib[nF], tip, -0.1);

        for (var q = 0; q < nF; q += 1) {
          tri(ob[q], ib[q], ib[q + 1], 0.15);
          tri(ob[q], ib[q + 1], ob[q + 1], 0.15);
        }

        var gt = SPIKE_GEMS[si];
        var gR = (RO + RI) / 2;
        var gr = 5.5;
        var gX = Math.cos(sA) * gR;
        var gZ = Math.sin(sA) * gR;
        var gY = BH + 2.5;
        var gv = [];

        for (var ri = 0; ri <= 5; ri += 1) {
          var phi = (ri / 5) * Math.PI;
          var row = [];

          for (var bi = 0; bi < 8; bi += 1) {
            var th = (bi / 8) * Math.PI * 2;
            row.push([
              gX + Math.sin(phi) * Math.cos(th) * gr,
              gY + Math.cos(phi) * gr,
              gZ + Math.sin(phi) * Math.sin(th) * gr
            ]);
          }

          gv.push(row);
        }

        for (var riA = 0; riA < 5; riA += 1) {
          for (var biA = 0; biA < 8; biA += 1) {
            var bjA = (biA + 1) % 8;
            tri(gv[riA][biA], gv[riA][bjA], gv[riA + 1][bjA], 0, true, gt);
            tri(gv[riA][biA], gv[riA + 1][bjA], gv[riA + 1][biA], 0, true, gt);
          }
        }

        var bR = 5.5;
        var bX = tip[0];
        var bZ = tip[2];
        var bY = tip[1] + bR * 0.7;
        var bv = [];

        for (var riB = 0; riB <= 5; riB += 1) {
          var phiB = (riB / 5) * Math.PI;
          var rowB = [];

          for (var biB = 0; biB < 8; biB += 1) {
            var thB = (biB / 8) * Math.PI * 2;
            rowB.push([
              bX + Math.sin(phiB) * Math.cos(thB) * bR,
              bY + Math.cos(phiB) * bR,
              bZ + Math.sin(phiB) * Math.sin(thB) * bR
            ]);
          }

          bv.push(rowB);
        }

        for (var riC = 0; riC < 5; riC += 1) {
          for (var biC = 0; biC < 8; biC += 1) {
            var bjC = (biC + 1) % 8;
            tri(bv[riC][biC], bv[riC][bjC], bv[riC + 1][bjC], 0, true, gt);
            tri(bv[riC][biC], bv[riC + 1][bjC], bv[riC + 1][biC], 0, true, gt);
          }
        }
      }

      polys.sort(function (a, b) {
        return a.z - b.z;
      });

      return polys;
    }

    function frame() {
      ct.clearRect(0, 0, 320, 300);
      bob += 0.018;
      var oy = Math.sin(bob) * 6;
      ct.save();
      ct.translate(0, oy);
      ct.save();
      ct.translate(CX, 275);
      ct.scale(1, 0.13);
      var sg = ct.createRadialGradient(0, 0, 0, 0, 0, 90);
      sg.addColorStop(0, "rgba(110,70,5,0.35)");
      sg.addColorStop(1, "rgba(110,70,5,0)");
      ct.fillStyle = sg;
      ct.beginPath();
      ct.arc(0, 0, 90, 0, Math.PI * 2);
      ct.fill();
      ct.restore();

      buildPolys(ang).forEach(function (p) {
        ct.beginPath();
        ct.moveTo(p.pts[0].x, p.pts[0].y);
        ct.lineTo(p.pts[1].x, p.pts[1].y);
        ct.lineTo(p.pts[2].x, p.pts[2].y);
        ct.closePath();
        ct.fillStyle = p.col;
        ct.fill();
        ct.strokeStyle = p.stroke;
        ct.lineWidth = 0.4;
        ct.stroke();
      });

      ct.restore();
      ang += 0.018;
      window.requestAnimationFrame(frame);
    }

    frame();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", function (event) {
    var section = event.target.querySelector("[data-fvh-section]");
    var canvas = event.target.querySelector("[data-fvh-crown-canvas]");

    if (section) {
      reveal(section);
    }

    if (canvas) {
      initCrown(canvas);
    }
  });
})();
